import { useEffect, useState } from "react";
import { FileText, Loader2, RefreshCw, Eye } from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import apiClient from "@/lib/api";
import RentalAgreementModal from "@/components/booking/RentalAgreementModal";
import { formatDate } from "@/components/booking/bookingMeta";

const STATUS = {
  draft: "bg-muted text-muted-foreground border-border",
  pending_acceptance: "bg-amber-100 text-amber-700 border-amber-200",
  accepted: "bg-emerald-100 text-emerald-700 border-emerald-200",
  cancelled: "bg-rose-100 text-rose-700 border-rose-200",
};
const LABEL = { draft: "Draft", pending_acceptance: "Menunggu", accepted: "Disetujui", cancelled: "Dibatalkan" };
const FILTERS = [
  { value: "all", label: "Semua" },
  { value: "pending_acceptance", label: "Menunggu" },
  { value: "accepted", label: "Disetujui" },
  { value: "cancelled", label: "Dibatalkan" },
];

export default function AdminAgreementsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [filter, setFilter] = useState("all");
  const [viewBooking, setViewBooking] = useState(null);

  const load = (status = filter) => {
    setLoading(true); setError(false);
    const qs = status && status !== "all" ? `?status=${status}` : "";
    apiClient.get(`/admin/agreements${qs}`)
      .then((r) => setRows(r.data || []))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(filter); /* eslint-disable-next-line */ }, [filter]);

  return (
    <AdminLayout testid="admin-agreements-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Rental Agreement</h1>
          <p className="mt-2 text-sm text-muted-foreground">Seluruh perjanjian sewa di platform AVASA.</p>
        </div>
        <button onClick={() => load(filter)} data-testid="admin-agreements-refresh"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold text-primary border border-border hover:bg-secondary/60 transition-colors">
          <RefreshCw size={14} /> Muat Ulang
        </button>
      </div>

      <div className="mt-6 flex flex-wrap gap-2" data-testid="admin-agreements-filters">
        {FILTERS.map((f) => (
          <button key={f.value} onClick={() => setFilter(f.value)}
            data-testid={`admin-agreements-filter-${f.value}`}
            className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${
              filter === f.value ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:bg-secondary/60"
            }`}>
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="mt-10 grid place-items-center py-16"><Loader2 className="animate-spin text-primary" size={28} /></div>
      ) : error ? (
        <div className="mt-8 rounded-2xl border border-destructive/30 bg-destructive/10 p-8 text-center" data-testid="admin-agreements-error">
          <p className="text-sm text-destructive">Gagal memuat agreement.</p>
          <button onClick={() => load(filter)} className="mt-3 text-sm font-semibold text-primary hover:underline">Coba lagi</button>
        </div>
      ) : rows.length === 0 ? (
        <div className="mt-6 rounded-2xl border border-dashed border-border bg-secondary/30 p-12 text-center" data-testid="admin-agreements-empty">
          <span className="grid place-items-center w-14 h-14 rounded-2xl bg-secondary text-primary mx-auto mb-4"><FileText size={26} /></span>
          <h3 className="font-heading text-lg font-medium text-foreground">Belum ada rental agreement.</h3>
        </div>
      ) : (
        <div className="mt-6 overflow-x-auto rounded-2xl border border-border" data-testid="admin-agreements-list">
          <table className="w-full text-sm">
            <thead className="bg-secondary/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-4 py-3">No. Agreement</th>
                <th className="px-4 py-3">Property</th>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Owner</th>
                <th className="px-4 py-3">Disetujui</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((a, i) => (
                <tr key={a.id} data-testid={`admin-agreement-${i}`} className="border-t border-border">
                  <td className="px-4 py-3 font-mono text-xs">{a.agreement_number}</td>
                  <td className="px-4 py-3 font-medium">{a.property_name || "-"}</td>
                  <td className="px-4 py-3">{a.customer_name || "-"}</td>
                  <td className="px-4 py-3">{a.owner_name || "-"}</td>
                  <td className="px-4 py-3 text-muted-foreground text-xs">{a.customer_accepted_at ? formatDate(a.customer_accepted_at) : "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full border ${STATUS[a.status] || ""}`}
                      data-testid={`admin-agreement-status-${i}`} data-agreement-status={a.status}>
                      {LABEL[a.status] || a.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => setViewBooking(a.booking_id)} data-testid={`admin-agreement-view-${i}`}
                      className="inline-flex items-center gap-1 text-primary font-semibold hover:underline">
                      <Eye size={14} /> Lihat
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <RentalAgreementModal
        open={!!viewBooking}
        bookingId={viewBooking}
        role="admin"
        onClose={() => setViewBooking(null)}
      />
    </AdminLayout>
  );
}
